// src/components/DashboardAdmin/ICTData/TourData.js
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchTourData,
  insertTourData,
  updateTourData,
  deleteTourData,
} from "../../../store/tourDataSlice";
import { fetchICTData } from "../../../store/uiSlice";
import { fetchEmployees } from "../../../store/empDataSlice";
import { fetchTrainingCards } from "../../../store/trainingCardsSlice";
import "../../../Styles/Common.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const TourData = () => {
  const dispatch = useDispatch();

  // Redux state
  const { tourData, loading, error } = useSelector((state) => state.tourData);
  const { ictData } = useSelector((state) => state.ui);
  const { employees } = useSelector((state) => state.empData);
  const { trainingCards } = useSelector((state) => state.trainingCards);

  // Local state for search, pagination, and form popup.
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);

  // Form state for insert/edit.
  // "id" is auto-generated on insert so it should be blank.
  const [formData, setFormData] = useState({
    id: "",
    tc_id: "",
    tourDesc: "",
    tourSDate: "",
    tourEDate: "",
    tourCity: "",
    tourCountry: "",
    tourCost: "",
    tourBill: null, // Will hold a File.
    countOfPerson: "",
    status: "A",
  });

  // Live search state for TC Id.
  const [tcSearchInput, setTcSearchInput] = useState("");
  const [tcSuggestions, setTcSuggestions] = useState([]);

  const itemsPerPage = 10;

  // Fetch initial data.
  useEffect(() => {
    dispatch(fetchTourData());
    dispatch(fetchICTData());
    dispatch(fetchEmployees());
    dispatch(fetchTrainingCards());
  }, [dispatch]);

  // --- Live search for TC Id (min 1 character) ---
  const handleTcSearchInputChange = (e) => {
    const value = e.target.value;
    setTcSearchInput(value);
    if (value.length >= 1) {
      const suggestions = trainingCards.filter((tc) =>
        String(tc.id).includes(value)
      );
      setTcSuggestions(suggestions);
    } else {
      setTcSuggestions([]);
    }
  };

  const handleSelectTcSuggestion = (tc) => {
    setFormData({ ...formData, tc_id: tc.id });
    setTcSearchInput(String(tc.id));
    setTcSuggestions([]);
  };

  // --- General form input change handler ---
  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // File upload handler for Tour Bill.
  const handleFileChange = (e) => {
    if (e.target.files.length > 0) {
      setFormData({ ...formData, tourBill: e.target.files[0] });
    }
  };

  // Helper to process formData.
  // In update mode, include the id; in insert mode, remove it.
  const processFormData = () => {
    if (editMode) {
      return {
        ...formData,
        tc_id: formData.tc_id ? Number(formData.tc_id) : 0,
        tourCost: formData.tourCost ? Number(formData.tourCost) : 0,
        countOfPerson: formData.countOfPerson ? Number(formData.countOfPerson) : 0,
      };
    } else {
      const { id, ...rest } = formData;
      return {
        ...rest,
        tc_id: rest.tc_id ? Number(rest.tc_id) : 0,
        tourCost: rest.tourCost ? Number(rest.tourCost) : 0,
        countOfPerson: rest.countOfPerson ? Number(rest.countOfPerson) : 0,
      };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const processedData = processFormData();
    console.log("Data to be sent:", processedData);
    try {
      if (editMode) {
        await dispatch(updateTourData(processedData)).unwrap();
        toast.success("Tour data updated successfully!");
      } else {
        await dispatch(insertTourData(processedData)).unwrap();
        toast.success("Tour data added successfully!");
      }
      setIsFormOpen(false);
      setFormData({
        id: "",
        tc_id: "",
        tourDesc: "",
        tourSDate: "",
        tourEDate: "",
        tourCity: "",
        tourCountry: "",
        tourCost: "",
        tourBill: null,
        countOfPerson: "",
        status: "A",
      });
      setTcSearchInput("");
      dispatch(fetchTourData());
    } catch (err) {
      console.error("Submit error:", err);
      toast.error("Failed to save tour data.");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this tour record?")) {
      try {
        await dispatch(deleteTourData(id)).unwrap();
        toast.success("Tour record deleted successfully!");
        dispatch(fetchTourData());
      } catch (err) {
        toast.error("Failed to delete tour data.");
      }
    }
  };

  // Filter tour records by tourDesc.
  const filteredTourData = tourData.filter((td) =>
    td.tourDesc?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const totalPages = Math.ceil(filteredTourData.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredTourData.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <div className="container">
      <div className="header-container">
        <h2>Tour Data</h2>
        <button
          className="add-lead-btn"
          onClick={() => {
            setIsFormOpen(true);
            setEditMode(false);
            setFormData({
              id: "",
              tc_id: "",
              tourDesc: "",
              tourSDate: "",
              tourEDate: "",
              tourCity: "",
              tourCountry: "",
              tourCost: "",
              tourBill: null,
              countOfPerson: "",
              status: "A",
            });
            setTcSearchInput("");
          }}
        >
          <i className="fa fa-plus"></i>
        </button>
      </div>
      <div className="feedback-filters">
        <input
          type="text"
          placeholder="Search by tour description"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="filter-input"
        />
      </div>
      {loading && <p>Loading tour data...</p>}
      {error && (
        <p className="error">
          {typeof error === "object" ? JSON.stringify(error) : error}
        </p>
      )}
      <table className="table">
        <thead>
          <tr>
            <th>#</th>
            <th>TC Id</th>
            <th>Description</th>
            <th>Start Date</th>
            <th>End Date</th>
            <th>City</th>
            <th>Country</th>
            <th>Cost</th>
            <th>Count</th>
            <th>Bill</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {currentItems.map((td, index) => (
            <tr key={td.id}>
              <td>{index + 1}</td>
              <td>{td.tc_id}</td>
              <td>{td.tourDesc}</td>
              <td>{td.tourSDate}</td>
              <td>{td.tourEDate}</td>
              <td>{td.tourCity}</td>
              <td>{td.tourCountry}</td>
              <td>
                {new Intl.NumberFormat("en-IN", {
                  style: "currency",
                  currency: "INR",
                }).format(td.tourCost)}
              </td>
              <td>{td.countOfPerson}</td>
              <td>
                {td.tourBill ? (
                  <a
                    href={`https://iteyonikservicesinstitute.com/download/documents/tourTrips/${td.tourBill.split("/").pop()}`}
                    download
                    title="Download Tour Bill"
                  >
                    <i className="fa fa-cloud-download" aria-hidden="true"></i>
                  </a>
                ) : (
                  "N/A"
                )}
              </td>
              <td>
                <button
                  onClick={() => handleDelete(td.id)}
                  className="icon-btn delete-btn"
                >
                  <i className="fa fa-trash" aria-hidden="true"></i>
                </button>
                <button
                  className="icon-btn update-btn"
                  onClick={() => {
                    setFormData({
                      id: td.id,
                      tc_id: td.tc_id,
                      tourDesc: td.tourDesc,
                      tourSDate: td.tourSDate?.split("T")[0] || "",
                      tourEDate: td.tourEDate?.split("T")[0] || "",
                      tourCity: td.tourCity,
                      tourCountry: td.tourCountry,
                      tourCost: td.tourCost,
                      countOfPerson: td.countOfPerson,
                      // Do not auto-fill file for update.
                      tourBill: null,
                      status: td.status,
                    });
                    setTcSearchInput(String(td.tc_id));
                    setEditMode(true);
                    setIsFormOpen(true);
                  }}
                >
                  <i className="fa fa-edit" aria-hidden="true"></i>
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {totalPages > 1 && (
        <div className="pagination">
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i + 1}
              className={currentPage === i + 1 ? "active" : ""}
              onClick={() => setCurrentPage(i + 1)}
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}
      {/* Insert/Edit Form Popup */}
      {isFormOpen && (
        <div className="popup-overlay">
          <div className="popup-content">
            <h3>{editMode ? "Edit Tour Data" : "Add Tour Data"}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Tour Description</label>
                <textarea
                  name="tourDesc"
                  placeholder="Enter tour description"
                  value={formData.tourDesc}
                  onChange={handleInputChange}
                  required
                ></textarea>
              </div>
              <div className="doubleFormField">
                <div className="form-group halfForm">
                  <label>Tour Start Date</label>
                  <input
                    type="date"
                    name="tourSDate"
                    value={formData.tourSDate || ""}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group halfForm">
                  <label>Tour End Date</label>
                  <input
                    type="date"
                    name="tourEDate"
                    value={formData.tourEDate || ""}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Tour City</label>
                <input
                  type="text"
                  name="tourCity"
                  placeholder="Enter tour city"
                  value={formData.tourCity}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Tour Country</label>
                <input
                  type="text"
                  name="tourCountry"
                  placeholder="Enter tour country"
                  value={formData.tourCountry}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Tour Cost</label>
                <input
                  type="number"
                  step="0.01"
                  name="tourCost"
                  placeholder="Enter Amount (INR)"
                  value={formData.tourCost}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Count Of Person</label>
                <input
                  type="number"
                  name="countOfPerson"
                  placeholder="Enter count"
                  value={formData.countOfPerson}
                  onChange={handleInputChange}
                  required
                />
              </div>
              {/* Live search for TC Id */}
              <div className="form-group">
                <label>TC Id</label>
                <input
                  type="text"
                  placeholder="Search TC Id"
                  value={tcSearchInput}
                  onChange={handleTcSearchInputChange}
                  required
                />
                {tcSuggestions.length > 0 && (
                  <div className="suggestions-dropdown">
                    {tcSuggestions.map((tc) => (
                      <div
                        key={tc.id}
                        className="suggestion-item"
                        onClick={() => handleSelectTcSuggestion(tc)}
                      >
                        {tc.id}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {/* File upload for Tour Bill */}
              <div className="form-group">
                <label>Tour Bill</label>
                <input
                  type="file"
                  name="tourBill"
                  accept=".pdf,.jpg,.jpeg,.doc,.docx"
                  onChange={handleFileChange}
                  required={!editMode}
                />
                {editMode && (
                  <p style={{ fontSize: "0.9em" }}>
                    If you don't select a new file, the old file remains.
                  </p>
                )}
              </div>
              <div className="form-actions">
                <button type="submit">
                  {editMode ? (
                    <i className="fa fa-pencil" aria-hidden="true"></i>
                  ) : (
                    <i className="fa fa-plus-square" aria-hidden="true"></i>
                  )}
                </button>
                <button
                  type="button"
                  className="fa fa-close"
                  onClick={() => setIsFormOpen(false)}
                >
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <ToastContainer position="bottom-right" autoClose={3000} />
    </div>
  );
};

export default TourData;
